import { useMemo, useEffect } from 'react';
import { useAuthStore } from '@rfacil/shared/services';
import { usePermissionsStore } from '@rfacil/shared/services';
import { getMaterialidadPermissions } from './permissions/materialidad-permissions';
import { getHostPermissions } from './permissions/host-permissions';
import { getRevisionPermissions } from './permissions/revision-permissions';
import type { RevisionPermissions } from './permissions/revision-permissions';
import { getBorradoPermissions } from './permissions/borrado-permissions';
import type { BorradoPermissions } from './permissions/borrado-permissions';
import { getConfirmacionPermissions } from './permissions/confirmacion-permissions';
import type { ConfirmacionPermissions } from './permissions/confirmacion-permissions';

/**
 * @function getPermissionsFromLocalStorage
 * @description Lee los permisos directamente de localStorage como fallback síncrono.
 *
 * En Module Federation, cada app (host y remotes) tiene su propia instancia de los stores
 * Zustand porque @rfacil/shared/services se empaqueta independientemente en cada app.
 * Cuando el host hace login y actualiza sus stores, los datos se persisten en localStorage
 * vía el middleware `persist` de Zustand. Sin embargo, las instancias de los remotes NO se
 * actualizan automáticamente.
 *
 * Esta función lee de localStorage sincronamente para proveer datos correctos durante
 * el primer render, evitando que PermissionRouteGuard redirija prematuramente.
 */
function getPermissionsFromLocalStorage(): {
  sections: string[];
  actions: string[];
  nombreRol: string | null;
} | null {
  try {
    const raw = localStorage.getItem('permissions-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const state = parsed?.state;
    if (!state) return null;
    if (state.sectionPermissions?.length > 0 || state.actionPermissions?.length > 0) {
      return {
        sections: state.sectionPermissions || [],
        actions: state.actionPermissions || [],
        nombreRol: state.nombreRol || null,
      };
    }
  } catch {
    // Si hay error al leer localStorage, retornar null (sin fallback)
  }
  return null;
}

/**
 * @type UserRole
 * @description Tipos de roles disponibles en el sistema
 */
export type UserRole = 'Super' | 'Corporativo' | 'Empresa' | 'Operativo';

/**
 * @interface RolePermissions
 * @description Interfaz que define todas las verificaciones de permisos basadas en roles
 */
export interface RolePermissions extends RevisionPermissions, BorradoPermissions, ConfirmacionPermissions {
  // Verificaciones de roles individuales
  isSuper: boolean;
  isCorporativo: boolean;
  isEmpresa: boolean;
  isUser: boolean;
  // Verificaciones de acceso a secciones de usuario SSO
  canViewCorporates: boolean;
  canViewCompanies: boolean;
  canViewCompanyEFirmas: boolean;
  canViewUsers: boolean;
  canViewRoles: boolean;
  canViewRelacionFacturaManual: boolean;
  /** Conciliación SAT: solo el rol Empresa */
  canViewConciliacionSat: boolean;
  /** Bandeja de solicitudes de reasignación: solo el rol Empresa */
  canViewSolicitudesReasignacion: boolean;
  /** Sección `Bitacora`: abre la pantalla de Auditoría y su item de menú */
  canViewAuditoria: boolean;
  /** Actividad `VerBitacora`: habilita el listado dentro de la pantalla de Auditoría */
  canViewListAuditoria: boolean;
  /** Buzón de notificaciones: solo los roles Operativo y Empresa reciben notificaciones */
  canViewBuzon: boolean;

  // Verificaciones de acceso a secciones con permisos de lista
  canViewManagementSectionMaterialidad: boolean;
  canViewManagementSection: boolean;
  canViewDocumentType: boolean;
  canViewInvoices: boolean;
  /** Sección FacturasVisor (listado amplio / visor) */
  canViewInvoicesVisor: boolean;
  canViewProviderUserRelation: boolean;
  canViewProviderType: boolean;

  // Verificaciones específicas de acciones se usuario de SSO
  canViewCorporateButtons: boolean;
  canViewRegularUserButtons: boolean;
  canCreateCorporateUsers: boolean;
  canCreateRegularUsers: boolean;
  canEditCorporateUsers: boolean;
  canEditRegularUsers: boolean;

  // Verificaciones específicas de acciones con permisos de lista
  canCreateDocumentType: boolean;
  canEditDocumentType: boolean;
  canDeleteDocumentType: boolean;
  canCreateProviderType: boolean;
  canEditProviderType: boolean;
  canDeleteProviderType: boolean;
  canCreateProviderUserRelation: boolean;
  canEditProviderUserRelation: boolean;
  canDeleteProviderUserRelation: boolean;
  canLoadInvoices: boolean;
  canViewDetailInvoices: boolean;
  canLoadDocumentInvoices: boolean;
  canAddTypeDocumentInvoices: boolean;
  canEditDocumnetInvoices: boolean;
  /** Actividad `EditarDescripcion`: habilita el lápiz de "Descripción del gasto" en el detalle de la factura */
  canEditDescriptionInvoices: boolean;
  /** Actividad `EditarDescripcionVisor`: habilita el mismo lápiz cuando el detalle se abrió desde el visor */
  canEditDescriptionInvoicesVisor: boolean;
  canAddTicketPaymentInvoices: boolean;
  canViewListDocumentType: boolean;
  canViewListProviderType: boolean;
  canViewListProviderUserRelation: boolean;
  canViewListInvoices: boolean;
  canViewListAllInvoices: boolean;
  canDeleteArchiveMaterialidad: boolean;
  canDeleteArchiveVisor: boolean;
  /** Actividad `EditarDocumentoVisor`: cambiar el tipo de documento en el detalle abierto desde el visor */
  canEditDocumentoVisor: boolean;
  canViewDetailVisor: boolean;

  // Utilidades
  currentRole: UserRole | null;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasAllRoles: (roles: UserRole[]) => boolean;

  // NUEVAS FUNCIONES - Verificar permisos de listas
  /**
   * @function hasSectionPermission
   * @description Verifica si una sección está en la lista de permisos de secciones
   * Si la lista está vacía, usa la lógica de roles existente
   */
  hasSectionPermission: (section: string) => boolean;

  /**
   * @function hasActionPermission
   * @description Verifica si una actividad está en la lista de permisos de actividades
   * Si la lista está vacía, retorna false
   */
  hasActionPermission: (action: string) => boolean;

  /**
   * @function getSectionPermissions
   * @description Obtiene la lista completa de permisos de secciones
   */
  getSectionPermissions: () => string[];

  /**
   * @function getActionPermissions
   * @description Obtiene la lista completa de permisos de actividades
   */
  getActionPermissions: () => string[];
}

/**
 * @hook useRolePermissions
 * @description Hook centralizado para verificar permisos basados en roles del usuario.
 * Combina permisos de todos los módulos de la suite (Materialidad, Host).
 * Ahora también verifica permisos de secciones y actividades desde listas del endpoint.
 *
 * Lógica:
 * - Si hay listas de permisos cargadas: verifica si está en la lista
 * - Si NO hay listas cargadas: usa la lógica de roles existente (comportamiento actual)
 *
 * Proporciona una API consistente y type-safe para verificar qué puede hacer el usuario actual.
 *
 * Este hook centraliza toda la lógica de verificación de roles que anteriormente estaba
 * duplicada en múltiples componentes (Navigation, TablaGestionUsuario, etc.).
 *
 * La estructura modular permite mantener los permisos organizados por módulo:
 * - host-permissions.ts: Permisos SSO y de gestión (DocumentType, ProviderType, etc.)
 * - materialidad-permissions.ts: Permisos de facturas/materialidad
 *
 * @returns {RolePermissions} Objeto con todas las verificaciones de permisos disponibles
 */
export const useRolePermissions = (): RolePermissions => {
  const user = useAuthStore((state) => state.user);
  const {
    sectionPermissions,
    actionPermissions,
    hasSectionPermission: checkSectionPermission,
    hasActionPermission: checkActionPermission,
  } = usePermissionsStore();

  // ─────────────────────────────────────────────────────────────────────────────
  // MODULE FEDERATION SYNC: Fallback síncrono a localStorage.
  //
  // En Module Federation, @rfacil/shared/services se empaqueta independientemente
  // en cada app (host y remotes). Esto crea instancias SEPARADAS de los stores
  // Zustand. Cuando el host hace login y llama fetchPermissions(), solo la
  // instancia del HOST se actualiza. Los remotes quedan con datos vacíos.
  //
  // Solución: Si el store en memoria está vacío, leemos de localStorage
  // sincronamente como fallback. Además, disparamos una re-hidratación asíncrona
  // para que futuros renders lean del store directamente.
  // ─────────────────────────────────────────────────────────────────────────────
  const needsSync = sectionPermissions.length === 0 && actionPermissions.length === 0;
  const localStorageFallback = needsSync ? getPermissionsFromLocalStorage() : null;

  useEffect(() => {
    if (needsSync && localStorageFallback) {
      try {
        usePermissionsStore.persist.rehydrate();
      } catch {
        usePermissionsStore.getState().setPermissions(
          localStorageFallback.sections,
          localStorageFallback.actions
        );
      }
    }
  }, [needsSync, localStorageFallback]);

  // Valores efectivos: usar store si tiene datos, sino localStorage como fallback
  const effectiveSectionPermissions = sectionPermissions.length > 0
    ? sectionPermissions
    : (localStorageFallback?.sections ?? []);
  const effectiveActionPermissions = actionPermissions.length > 0
    ? actionPermissions
    : (localStorageFallback?.actions ?? []);

  return useMemo(() => {
    const userRole = user?.role as UserRole;
    const normalizedRole = userRole || null;
    const isSuper = normalizedRole === 'Super';
    const isCorporativo = normalizedRole === 'Corporativo';
    const isEmpresa = normalizedRole === 'Empresa';
    const isUser = normalizedRole === 'Operativo';

    // Helper: Combina verificación de rol con verificación de lista de permisos
    // OBLIGATORIO: Debe cumplir AMBAS condiciones (rol Y permiso de lista)
    const canViewByRoleOrSectionList = (
      roleCheck: boolean,
      sectionKey: string
    ): boolean => {
      if (effectiveSectionPermissions.length > 0) {
        return roleCheck && effectiveSectionPermissions.some(
          (perm) => perm.toLowerCase().trim() === sectionKey.toLowerCase().trim()
        );
      }
      // Si no hay listas de permisos cargadas, el usuario no tiene acceso
      return false;
    };
    // OBLIGATORIO: Debe cumplir AMBAS condiciones (rol Y permiso de lista)
    const canPerformByRoleOrActionList = (
      roleCheck: boolean,
      actionKey: string
    ): boolean => {
      if (effectiveActionPermissions.length > 0) {
        return roleCheck && effectiveActionPermissions.some(
          (perm) => perm.toLowerCase().trim() === actionKey.toLowerCase().trim()
        );
      }
      // Si no hay listas de permisos cargadas, el usuario no tiene acceso
      return false;
    };

    // Obtener permisos de cada módulo
    const materialidadPermissions = getMaterialidadPermissions(
      isUser,
      canViewByRoleOrSectionList,
      canPerformByRoleOrActionList
    );

    const hostPermissions = getHostPermissions(
      isSuper,
      isCorporativo,
      isEmpresa,
      isUser,
      canViewByRoleOrSectionList,
      canPerformByRoleOrActionList
    );

    const revisionPermissions = getRevisionPermissions(isEmpresa, isUser, canPerformByRoleOrActionList);
    const borradoPermissions = getBorradoPermissions(isEmpresa, isUser, canPerformByRoleOrActionList);
    const confirmacionPermissions = getConfirmacionPermissions(isEmpresa, isUser);

    const hasAnyRole = (roles: UserRole[]): boolean => {
      if (!normalizedRole) return false;
      return roles.some(
        (role) => role.toLowerCase() === normalizedRole.toLowerCase()
      );
    };

    const hasAllRoles = (roles: UserRole[]): boolean => {
      if (!normalizedRole) return false;
      // Para un solo usuario, solo puede tener un rol, pero esta función
      // permite verificar si el rol está en la lista
      return roles.some(
        (role) => role.toLowerCase() === normalizedRole.toLowerCase()
      );
    };

    // NUEVAS FUNCIONES - Verificar directamente en las listas
    // Usa effectivePermissions para cubrir el fallback de localStorage en Module Federation
    const hasSectionPermission = (section: string): boolean => {
      if (effectiveSectionPermissions.length > 0) {
        return effectiveSectionPermissions.some(
          (perm) => perm.toLowerCase().trim() === section.toLowerCase().trim()
        );
      }
      return checkSectionPermission(section);
    };

    const hasActionPermission = (action: string): boolean => {
      if (effectiveActionPermissions.length > 0) {
        return effectiveActionPermissions.some(
          (perm) => perm.toLowerCase().trim() === action.toLowerCase().trim()
        );
      }
      return checkActionPermission(action);
    };

    const getSectionPermissions = (): string[] => {
      return effectiveSectionPermissions;
    };

    const getActionPermissions = (): string[] => {
      return effectiveActionPermissions;
    };

    // Combinar todos los permisos de todos los módulos
    return {
      // Roles individuales
      isSuper,
      isCorporativo,
      isEmpresa,
      isUser,

      // Permisos de todos los módulos (se combinan automáticamente)
      ...materialidadPermissions,
      ...hostPermissions,
      ...revisionPermissions,
      ...borradoPermissions,
      ...confirmacionPermissions,

      // Utilidades
      currentRole: normalizedRole,
      hasAnyRole,
      hasAllRoles,
      hasSectionPermission,
      hasActionPermission,
      getSectionPermissions,
      getActionPermissions,
    } as RolePermissions;
  }, [
    user?.role,
    effectiveSectionPermissions,
    effectiveActionPermissions,
    sectionPermissions,
    actionPermissions,
    checkSectionPermission,
    checkActionPermission,
  ]);
};
